// SPDX-License-Identifier: AGPL-3.0-or-later

import styles from '@app/features/app/components/dialogs/components/plutonium/PricingContextPanel.module.css';
import {ToggleButton} from '@app/features/app/components/dialogs/components/ToggleButton';
import {
	BLIK_PAYMENT_METHOD,
	MB_WAY_PAYMENT_METHOD,
	PAYMENT_PROVIDER_NAME,
	PIX_PAYMENT_METHOD,
	UPI_PAYMENT_METHOD,
} from '@app/features/app/config/I18nDisplayConstants';
import {getCurrencyCodeLabel, type PricingMode} from '@app/features/premium/utils/PricingUtils';
import {Trans, useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import type React from 'react';
import {useMemo} from 'react';

interface PricingContextPanelProps {
	countryCode: string | null;
	pricingMode: PricingMode;
	setPricingMode: (mode: PricingMode) => void;
	hasPricingChoice: boolean;
	localizedCurrency: string | null;
	baseCurrency: string | null;
	isGiftMode: boolean;
	compact?: boolean;
}
function getCountryContext(countryCode: string | null): 'BR' | 'IN' | 'PL' | 'PT' | 'TR' | 'OTHER' {
	const upperCountryCode = countryCode?.toUpperCase();
	switch (upperCountryCode) {
		case 'BR':
		case 'IN':
		case 'PL':
		case 'PT':
		case 'TR':
			return upperCountryCode;
		default:
			return 'OTHER';
	}
}
export const PricingContextPanel: React.FC<PricingContextPanelProps> = observer(
	({
		countryCode,
		pricingMode,
		setPricingMode,
		hasPricingChoice,
		localizedCurrency,
		baseCurrency,
		isGiftMode,
		compact = false,
	}) => {
		const {t} = useLingui();
		const countryContext = getCountryContext(countryCode);
		const localizedCurrencyLabel = getCurrencyCodeLabel(localizedCurrency);
		const baseCurrencyLabel = getCurrencyCodeLabel(baseCurrency);
		const guidance = useMemo(() => {
			if (isGiftMode) {
				if (pricingMode === 'localized') {
					switch (countryContext) {
						case 'BR':
							return {
								title: t`Localized BRL gift checkout`,
								items: [
									t`${PIX_PAYMENT_METHOD} can be used for one-time BRL payments when ${PAYMENT_PROVIDER_NAME} offers it on the checkout page.`,
									t`If you pay with a card in BRL, it still needs to be issued in Brazil for the purchase to stay eligible.`,
								],
							};
						case 'IN':
							return {
								title: t`Localized INR gift checkout`,
								items: [
									t`${UPI_PAYMENT_METHOD} can be used for one-time INR payments when ${PAYMENT_PROVIDER_NAME} offers it on the checkout page.`,
									t`If you pay with a card in INR, it still needs to be issued in India for the purchase to stay eligible.`,
								],
							};
						case 'PL':
							return {
								title: t`Localized PLN gift checkout`,
								items: [
									t`${BLIK_PAYMENT_METHOD} can be used for one-time PLN gift payments, even though it does not support subscriptions in ${PAYMENT_PROVIDER_NAME} Checkout.`,
									t`If you pay with a card in PLN, it still needs to be issued in Poland for the purchase to stay eligible.`,
								],
							};
						case 'TR':
							return {
								title: t`Localized TRY gift checkout`,
								items: [
									t`TRY gifts are one-time payments. Cards charged in TRY still need to be issued in Türkiye for the purchase to stay eligible.`,
									t`There is no app-based local payment method surfaced here today, so card checkout is the main path.`,
								],
							};
					}
				}
				if (countryContext === 'PT' && baseCurrency === 'EUR') {
					return {
						title: t`EUR gift checkout`,
						items: [
							t`${MB_WAY_PAYMENT_METHOD} can be used for one-time EUR gift payments when ${PAYMENT_PROVIDER_NAME} offers it on the checkout page.`,
						],
					};
				}
				if (hasPricingChoice && pricingMode === 'base') {
					return {
						title: t`Standard ${baseCurrencyLabel} gift checkout`,
						items: [t`Switch back to ${localizedCurrencyLabel} if you prefer the localized price instead.`],
					};
				}
				return null;
			}
			if (countryContext === 'PT' && baseCurrency === 'EUR') {
				return {
					title: t`EUR subscription checkout`,
					items: [
						t`${MB_WAY_PAYMENT_METHOD} is a one-time payment method in ${PAYMENT_PROVIDER_NAME} Checkout, so it is not a recurring subscription path here.`,
						t`Use a card for the subscription, or buy a gift instead if you want a one-time EUR payment method.`,
					],
				};
			}
			if (pricingMode === 'base') {
				if (hasPricingChoice) {
					return {
						title: t`Standard ${baseCurrencyLabel} pricing`,
						items: [
							t`You are using the full standard ${baseCurrencyLabel} price instead of localized pricing.`,
							t`This skips localized card verification. Switch back to ${localizedCurrencyLabel} if you prefer the local price.`,
						],
					};
				}
				return null;
			}
			switch (countryContext) {
				case 'BR':
					return {
						title: t`Localized BRL subscription checkout`,
						items: [
							t`Cards can keep BRL pricing after a quick ${PAYMENT_PROVIDER_NAME} verification, but the card must be issued in Brazil.`,
							t`${PIX_PAYMENT_METHOD} can skip the card verification step when ${PAYMENT_PROVIDER_NAME} offers recurring ${PIX_PAYMENT_METHOD} for your checkout.`,
							t`If ${PIX_PAYMENT_METHOD} is unavailable or you prefer standard pricing, you can switch to USD at any time.`,
						],
					};
				case 'IN':
					return {
						title: t`Localized INR subscription checkout`,
						items: [
							t`Cards can keep INR pricing after a quick ${PAYMENT_PROVIDER_NAME} verification, but the card must be issued in India.`,
							t`${UPI_PAYMENT_METHOD} can skip the card verification step when ${PAYMENT_PROVIDER_NAME} offers recurring ${UPI_PAYMENT_METHOD} for your checkout.`,
							t`If recurring ${UPI_PAYMENT_METHOD} is unavailable or you prefer standard pricing, you can switch to USD at any time.`,
						],
					};
				case 'PL':
					return {
						title: t`Localized PLN subscription checkout`,
						items: [
							t`Cards can keep PLN pricing after a quick ${PAYMENT_PROVIDER_NAME} verification, but the card must be issued in Poland.`,
							t`${BLIK_PAYMENT_METHOD} does not support subscriptions in ${PAYMENT_PROVIDER_NAME} Checkout.`,
							t`If you want to use ${BLIK_PAYMENT_METHOD}, buy a PLN gift instead, or switch to standard EUR pricing.`,
						],
					};
				case 'TR':
					return {
						title: t`Localized TRY subscription checkout`,
						items: [
							t`Cards can keep TRY pricing after a quick ${PAYMENT_PROVIDER_NAME} verification, but the card must be issued in Türkiye.`,
							t`There is no app-based local subscription method surfaced here today, so local card checkout is the main path.`,
							t`If you prefer, you can switch to standard USD pricing instead.`,
						],
					};
				default:
					return null;
			}
		}, [
			baseCurrency,
			baseCurrencyLabel,
			countryContext,
			hasPricingChoice,
			isGiftMode,
			localizedCurrencyLabel,
			pricingMode,
			t,
		]);
		const summary = useMemo(() => {
			if (pricingMode === 'localized') {
				if (hasPricingChoice) {
					return t`Using localized ${localizedCurrencyLabel} pricing.`;
				}
				return null;
			}
			if (hasPricingChoice) {
				return t`Using standard ${baseCurrencyLabel} pricing.`;
			}
			return null;
		}, [baseCurrencyLabel, hasPricingChoice, localizedCurrencyLabel, pricingMode, t]);
		return (
			<div className={styles.container}>
				{hasPricingChoice && (
					<div className={styles.toggleContainer} role="group" aria-label={t`Pricing preference`}>
						<ToggleButton
							active={pricingMode === 'localized'}
							onClick={() => setPricingMode('localized')}
							label={<Trans>Local {localizedCurrencyLabel}</Trans>}
						/>
						<ToggleButton
							active={pricingMode === 'base'}
							onClick={() => setPricingMode('base')}
							label={<Trans>Standard {baseCurrencyLabel}</Trans>}
						/>
					</div>
				)}
				{summary && compact && <p className={styles.summary}>{summary}</p>}
				{guidance && !compact && (
					<div className={clsx(styles.panel, compact && styles.panelCompact)}>
						<h3 className={styles.title}>{guidance.title}</h3>
						<ul className={styles.list}>
							{guidance.items.map((item) => (
								<li key={item}>{item}</li>
							))}
						</ul>
					</div>
				)}
			</div>
		);
	},
);
